/** Jalali (Solar Hijri) calendar conversion. */

export interface JalaliDate {
  /** Jalali year, for example 1405 */
  jy: number
  /** Jalali month, 1 to 12 */
  jm: number
  /** Jalali day of month, 1 to 31 */
  jd: number
}

const BREAKS = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192,
  2262, 2324, 2394, 2456, 3178,
]

interface CalCache {
  leap: number
  gy: number
  march: number
}

function jalCal(jy: number): CalCache {
  const bl = BREAKS.length
  const gy = jy + 621
  let leapJ = -14
  let jp = BREAKS[0]

  if (jy < jp || jy >= BREAKS[bl - 1]) {
    throw new RangeError(`jalali year out of range: ${jy}`)
  }

  let jump = 0
  for (let i = 1; i < bl; i += 1) {
    const jm = BREAKS[i]
    jump = jm - jp
    if (jy < jm) break
    leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4)
    jp = jm
  }
  let n = jy - jp

  leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4)
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1

  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150
  const march = 20 + leapJ - leapG

  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33
  let leap = mod(mod(n + 1, 33) - 1, 4)
  if (leap === -1) leap = 4

  return { leap, gy, march }
}

function div(a: number, b: number): number {
  return ~~(a / b)
}

function mod(a: number, b: number): number {
  return a - ~~(a / b) * b
}

/** Gregorian calendar date to Julian Day Number. */
export function g2d(gy: number, gm: number, gd: number): number {
  let d =
    div((gy + div(gm - 8, 6) + 100100) * 1461, 4) +
    div(153 * mod(gm + 9, 12) + 2, 5) +
    gd -
    34840408
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752
  return d
}

/** Julian Day Number to Gregorian calendar date. */
export function d2g(jdn: number): { gy: number; gm: number; gd: number } {
  let j = 4 * jdn + 139361631
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908
  const i = div(mod(j, 1461), 4) * 5 + 308
  const gd = div(mod(i, 153), 5) + 1
  const gm = mod(div(i, 153), 12) + 1
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6)
  return { gy, gm, gd }
}

/** Jalali calendar date to Julian Day Number. */
export function j2d(jy: number, jm: number, jd: number): number {
  const r = jalCal(jy)
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1
}

/** Julian Day Number to Jalali calendar date. */
export function d2j(jdn: number): JalaliDate {
  let gy = d2g(jdn).gy
  let jy = gy - 621
  const r = jalCal(jy)
  const jdn1f = g2d(gy, 3, r.march)

  let k = jdn - jdn1f
  if (k >= 0) {
    if (k <= 185) {
      const jm = 1 + div(k, 31)
      const jd = mod(k, 31) + 1
      return { jy, jm, jd }
    }
    k -= 186
  } else {
    jy -= 1
    k += 179
    if (r.leap === 1) k += 1
  }
  const jm = 7 + div(k, 30)
  const jd = mod(k, 30) + 1
  return { jy, jm, jd }
}

/** Convert a JavaScript Date, read in the given IANA zone, to a Jalali date. */
export function toJalali(date: Date, timeZone = 'Asia/Tehran'): JalaliDate {
  const parts = zonedParts(date, timeZone)
  return d2j(g2d(parts.year, parts.month, parts.day))
}

/** Convert a Jalali date plus a wall clock time in Tehran to a Date. */
export function fromJalali(
  jy: number,
  jm: number,
  jd: number,
  hour = 0,
  minute = 0,
  second = 0,
  timeZone = 'Asia/Tehran',
): Date {
  const { gy, gm, gd } = d2g(j2d(jy, jm, jd))
  const naive = Date.UTC(gy, gm - 1, gd, hour, minute, second)
  // Correct for the zone offset at that instant.
  const offset = zoneOffsetMs(new Date(naive), timeZone)
  return new Date(naive - offset)
}

export const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const

export const WEEKDAYS = [
  'شنبه',
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنجشنبه',
  'جمعه',
] as const

/** Weekday index where 0 is شنبه, matching the Iranian week. */
export function jalaliWeekday(date: Date, timeZone = 'Asia/Tehran'): number {
  const p = zonedParts(date, timeZone)
  const g = new Date(Date.UTC(p.year, p.month - 1, p.day))
  // getUTCDay: 0 is Sunday. شنبه is Saturday, so shift by one.
  return (g.getUTCDay() + 1) % 7
}

interface ZonedParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const partFormatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = partFormatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US-u-ca-gregory', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    partFormatters.set(timeZone, f)
  }
  return f
}

/** Calendar and clock fields of an instant, as read in a given zone. */
export function zonedParts(date: Date, timeZone = 'Asia/Tehran'): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(date)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0')
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  }
}

/** Offset of a zone from UTC at a given instant, in milliseconds. */
export function zoneOffsetMs(date: Date, timeZone = 'Asia/Tehran'): number {
  const p = zonedParts(date, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(date.getTime() / 1000) * 1000
}
